import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import {
  formatSizeDimensions,
  SIZE_IMAGE_MAP,
  SizeGuideEntry
} from 'src/components/Modals/StorageSizeModal/constants'

type Props = {
  size: SizeGuideEntry
  alt: string
  sizes: string
}

/** The floor diagram is proportional; the generated contents are illustrative. */
export default function SizeIllustration({ size, alt, sizes }: Props) {
  const { t } = useTranslation('size_guide')
  const { width, length } = size.dimensions
  const scale = Math.min(112 / length, 44 / width)
  const planWidth = length * scale
  const planHeight = width * scale

  return (
    <figure className="overflow-hidden border border-ink-100/15 bg-cream-200">
      <div className="relative aspect-[3/2]">
        <Image
          key={size.id}
          src={SIZE_IMAGE_MAP[size.i18nKey]}
          alt={alt}
          fill
          sizes={sizes}
          className="object-contain"
          quality={85}
        />
      </div>
      <figcaption className="flex items-center justify-between gap-3 border-t border-ink-100/15 bg-white-500 px-4 py-3">
        <div className="min-w-0">
          <p className="text-[14px] font-medium text-ink-700">
            {formatSizeDimensions(size.dimensions)}
          </p>
          <p className="mt-1 text-[11px] leading-relaxed text-ink-200">
            {t('FLOOR_PROPORTIONS')}
          </p>
        </div>
        <svg
          viewBox="0 0 128 60"
          className="h-12 w-24 shrink-0 text-blue-500"
          aria-hidden="true"
          focusable="false"
        >
          <rect
            x={(128 - planWidth) / 2}
            y={(60 - planHeight) / 2}
            width={planWidth}
            height={planHeight}
            fill="currentColor"
            fillOpacity="0.08"
            stroke="currentColor"
            strokeWidth="2"
          />
        </svg>
      </figcaption>
    </figure>
  )
}
